import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

export type AlertStatus = 'ok' | 'attention' | 'critical';

interface AlertThresholds {
  globalMinimum: number;
  categoryMinimums: Record<string, number>;
}

interface AlertSettingsContextValue {
  thresholds: AlertThresholds;
  setGlobalMinimum: (value: number) => void;
  setCategoryMinimum: (category: string, value: number) => void;
  removeCategoryMinimum: (category: string) => void;
  getThresholdForCategory: (category?: string | null) => number;
  getStatusForQuantity: (quantity: number | undefined, category?: string | null) => AlertStatus;
}

const DEFAULT_THRESHOLDS: AlertThresholds = {
  globalMinimum: 5,
  categoryMinimums: {},
};

const STORAGE_KEY = 'stockflow-alert-thresholds';

const AlertSettingsContext = createContext<AlertSettingsContextValue | undefined>(undefined);

export const AlertSettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [thresholds, setThresholds] = useState<AlertThresholds>(DEFAULT_THRESHOLDS);

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return;

    try {
      const parsed = JSON.parse(saved) as AlertThresholds;
      if (parsed && typeof parsed.globalMinimum === 'number' && parsed.categoryMinimums) {
        setThresholds({
          globalMinimum: parsed.globalMinimum,
          categoryMinimums: parsed.categoryMinimums,
        });
      }
    } catch (error) {
      console.error('Failed to parse alert thresholds', error);
    }
  }, []);

  const persist = (next: AlertThresholds) => {
    setThresholds(next);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  };

  const setGlobalMinimum = (value: number) => {
    const safeValue = Math.max(0, Math.round(value));
    persist({
      ...thresholds,
      globalMinimum: safeValue,
    });
  };

  const setCategoryMinimum = (category: string, value: number) => {
    if (!category.trim()) return;
    const safeValue = Math.max(0, Math.round(value));
    const key = category.trim();
    persist({
      ...thresholds,
      categoryMinimums: {
        ...thresholds.categoryMinimums,
        [key]: safeValue,
      },
    });
  };

  const removeCategoryMinimum = (category: string) => {
    const next = { ...thresholds.categoryMinimums };
    delete next[category];
    persist({
      ...thresholds,
      categoryMinimums: next,
    });
  };

  const getThresholdForCategory = (category?: string | null) => {
    if (category && thresholds.categoryMinimums[category]) {
      return thresholds.categoryMinimums[category];
    }
    return thresholds.globalMinimum;
  };

  const getStatusForQuantity = (quantity: number | undefined, category?: string | null): AlertStatus => {
    if (quantity === undefined || quantity === null) return 'ok';
    const threshold = getThresholdForCategory(category);
    if (quantity <= threshold) return 'critical';

    const attentionBuffer = Math.max(1, Math.round(threshold * 0.5));
    if (quantity <= threshold + attentionBuffer) return 'attention';

    return 'ok';
  };

  const value = useMemo<AlertSettingsContextValue>(
    () => ({
      thresholds,
      setGlobalMinimum,
      setCategoryMinimum,
      removeCategoryMinimum,
      getThresholdForCategory,
      getStatusForQuantity,
    }),
    [thresholds]
  );

  return (
    <AlertSettingsContext.Provider value={value}>
      {children}
    </AlertSettingsContext.Provider>
  );
};

export const useAlertSettings = () => {
  const context = useContext(AlertSettingsContext);
  if (!context) throw new Error('useAlertSettings must be used within AlertSettingsProvider');
  return context;
};
